<template>
  <div class="sj-number-input" :class="[theme, { disabled }, size]">
    <sj-input
      ref="input"
      :value="isFocused || mouseTimer ? number : formattedNumber"
      @keyup.enter.native="handleEnter"
      @input="number = $event"
      @focus="handleFocus"
      @blur="handleBlur"
      :disabled="disabled"
      :size="size"
      :padding="0"
      v-bind="$attrs"
    />
    <div class="spinner-group">
      <div
        class="spinner"
        @mousedown="handleMousedown('add')"
        @mouseup="handleMouseup('add')"
        @mouseout="handleMouseout"
      >
        <i class="icon_com comselect_back" />
      </div>
      <div class="divider" />
      <div
        class="spinner"
        @mousedown="handleMousedown('minus')"
        @mouseup="handleMouseup('minus')"
        @mouseout="handleMouseout"
      >
        <i class="icon_com comselect_expand" />
      </div>
    </div>
  </div>
</template>

<script>
import { isNumeric } from "../../../utils";
const math = require("mathjs");
import sizeMixin from "../../../utils/sizeMixin";
export default {
  name: "SjNumberInput",
  mixins: [sizeMixin],
  props: {
    theme: {
      type: String,
      default: "dark" //dark light background shadow
    },
    value: [Number],
    formatter: {
      type: Function,
      default(val) {
        return val?.toLocaleString(undefined, {
          maximumFractionDigits: 2
        });
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    validator: {
      type: Function,
      default() {
        return true;
      }
    },
    interval: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      isFocused: false,

      number: null,

      mouseTimer: null,
      mousedownTime: null,
      timeDelay: 500
    };
  },
  computed: {
    formattedNumber() {
      if (!isNumeric(this.value)) return null;
      if (this.formatter && this.formatter instanceof Function) {
        return this.formatter(this.value);
      }
      return this.value;
    }
  },
  methods: {
    handleFocus() {
      this.isFocused = true;
      this.$emit("focus");
    },
    handleBlur() {
      this.isFocused = false;
      this.checkNumber();
      this.$emit("blur");
    },

    checkNumber() {
      if (isNumeric(this.number)) {
        let checked = true;
        const number = Number(this.number);
        if (this.validator && this.validator instanceof Function) {
          checked = this.validator(number);
        }
        if (checked) {
          this.$emit("input", number);
          this.$emit("change", number);
        } else {
          this.getNumberFromValue();
        }
      }
    },

    handleEnter() {
      this.$refs.input.blur();
    },

    handleMousedown(type) {
      if (this.disabled) return;
      this.mousedownTime = new Date();

      const timer = () => {
        this.handle(type);
        this.mouseTimer = setTimeout(() => {
          timer();
        }, 100);
      };
      this.mouseTimer = setTimeout(() => {
        timer();
      }, this.timeDelay);
    },

    handleMouseup(type) {
      if (this.disabled) return;
      if (new Date() - this.mousedownTime < this.timeDelay) {
        this.handle(type);
      }
      this.clearTimer();
    },

    handleMouseout() {
      this.clearTimer();
    },

    clearTimer(handleChange = true) {
      if (this.mouseTimer) {
        clearTimeout(this.mouseTimer);
        this.mouseTimer = null;
        if (handleChange) {
          this.checkNumber();
        }
      }
    },

    handle(type) {
      if (!isNumeric(this.number)) return;
      let result;
      if (type === "add") {
        result = math.number(
          math.add(math.bignumber(this.number), math.bignumber(this.interval))
        );
      } else {
        result = math.number(
          math.subtract(
            math.bignumber(this.number),
            math.bignumber(this.interval)
          )
        );
      }
      this.number = result;
    },

    getNumberFromValue() {
      const { value } = this;
      if (!isNumeric(value)) {
        this.number = null;
      } else {
        this.number = String(value);
      }
    }
  },
  beforeDestroy() {
    this.clearTimer(false);
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.getNumberFromValue();
      }
    }
  }
};
</script>

<style scoped lang="less">
.sj-number-input {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 0 0 10px;
  width: 120px;
  border-radius: @base-border-radius;
  .spinner-border-color(@color) {
    &:not(.disabled) {
      .spinner-group {
        &:hover {
          border-left-color: @color;

          .divider {
            background-color: @color;
          }
        }
      }
    }
  }
  &.shadow {
    box-shadow: 0 0 3px 2px rgba(0, 0, 0, 0.03);
    border: 1px solid @color-gray-3;
    background-color: @color-antibg;
    .spinner-border-color(@color-gray-3);
  }
  &.background {
    background-color: @color-gray-2;
  }
  &.light {
    border: 1px solid @color-productlight;
    .spinner-border-color(@color-productlight);
  }
  &.dark {
    border: 1px solid @color-gray-3;
    .spinner-border-color(@color-gray-3);
  }
  .input {
    flex-grow: 1;
    min-width: 0;
  }
  &.large,
  &.middle {
    .spinner-group {
      width: 20px;
      .spinner {
        i {
          height: 16px;
          font-size: 16px;
          line-height: 16px;
        }
      }
    }
  }
  &.default,
  &.mini,
  &.small {
    .spinner-group {
      width: 16px;
    }
  }
  &.mini {
    .spinner-group {
      .spinner {
        height: 10px;
        i {
          transform: scale(10/12);
        }
      }
    }
  }
  .spinner-group {
    border-left: 1px solid transparent;
    flex-shrink: 0;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    align-self: stretch;
    .spinner {
      flex-grow: 1;
      min-height: 0;
      color: @color-normal;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      &:hover,
      &:active {
        color: @color-highlight;
      }
      i {
        font-size: 12px;
        height: 12px;
        line-height: 12px;
      }
    }
    .divider {
      flex-shrink: 0;
      height: 1px;
      background-color: transparent;
    }
  }

  &.disabled {
    cursor: not-allowed;
    .spinner-group {
      cursor: not-allowed;
      .spinner {
        color: @color-light;
      }
    }
  }
}
</style>
